import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, FileDown, CalendarDays, Megaphone, User } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import type { SearchSelectOption } from '../../Shared/SearchSelect';
import ReportCreateModal from '../../../pages/OpenItems/ReportCreateModal';
import { SectionHead } from '../SectionHead';

interface QuickItemBase {
  key: string;
  label: string;
  icon: typeof CalendarPlus;
  accent: string;
}

/** A tile either navigates (`to`) or triggers an action in place (`onClick`, e.g. a modal). */
type QuickItem = QuickItemBase & ({ to: string } | { onClick: () => void });

const TILE_CLASS =
  'group flex flex-col items-start gap-2 rounded-lg border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-3 text-left hover:border-primary hover:shadow-sm transition-all';

interface QuickActionsCardProps {
  /** Objects of today's assignments — offered first when reporting something. */
  reportObjectSuggestions?: SearchSelectOption[];
}

export function QuickActionsCard({ reportObjectSuggestions = [] }: QuickActionsCardProps) {
  const { t } = useTranslation('dashboard');
  const [isReportOpen, setIsReportOpen] = useState(false);

  const items: QuickItem[] = [
    {
      key: 'report',
      onClick: () => setIsReportOpen(true),
      label: t('quick.report'),
      icon: Megaphone,
      accent: 'text-rose-600 bg-rose-100 dark:bg-rose-900/20',
    },
    {
      key: 'absence',
      to: '/absences',
      label: t('quick.myAbsence'),
      icon: CalendarPlus,
      accent: 'text-accent bg-accent/10 dark:bg-accent/20',
    },
    {
      key: 'plan',
      to: '/my-plan',
      label: t('quick.myPlan'),
      icon: CalendarDays,
      accent: 'text-primary bg-primary/10 dark:bg-primary/20',
    },
    {
      key: 'reports',
      to: '/timeclock',
      label: t('quick.myReports'),
      icon: FileDown,
      accent: 'text-purple-600 bg-purple-100 dark:bg-purple-900/20',
    },
    {
      key: 'profile',
      to: '/profile',
      label: t('quick.profile'),
      icon: User,
      accent: 'text-amber-600 bg-amber-100 dark:bg-amber-900/20',
    },
  ];

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      <SectionHead title={t('quick.title')} />
      <div className="mt-4 grid grid-cols-2 gap-2">
        {items.map((item) => {
          const { key, label, icon: Icon, accent } = item;
          const content = (
            <>
              <span className={`inline-flex w-8 h-8 items-center justify-center rounded-lg ${accent}`}>
                <Icon className="w-4 h-4" aria-hidden />
              </span>
              <span className="text-sm font-medium text-ink dark:text-slate-50">{label}</span>
            </>
          );
          return 'to' in item ? (
            <Link key={key} to={item.to} className={TILE_CLASS}>
              {content}
            </Link>
          ) : (
            // The one action tile spans the row: four links fill two rows, and it is the quickest way in.
            <button key={key} type="button" onClick={item.onClick} className={`${TILE_CLASS} col-span-2`}>
              {content}
            </button>
          );
        })}
      </div>

      <ReportCreateModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        suggestedObjects={reportObjectSuggestions}
      />
    </div>
  );
}
